import axios, {
    AxiosHeaders,
    type AxiosInstance,
    type AxiosError,
    type InternalAxiosRequestConfig,
} from 'axios';
import { API_BASE_URL } from '../types';
import {
    reportSellerSessionDrop,
    setSentrySellerUser,
    suppressSellerSessionDropReports,
} from '../../sentry';

/** Отдельные ключи: не пересекаются с обычной админкой (`access_token`). */
const ACCESS_TOKEN_KEY = 'luxus_seller_access_token';
const REFRESH_TOKEN_KEY = 'luxus_seller_refresh_token';
/** Старый ключ до разделения сессий — мигрируем только user JWT. */
const LEGACY_ACCESS_TOKEN_KEY = 'access_token';

type JwtPayload = {
    sub?: number | string;
    type?: string;
    email?: string;
    phone?: string;
};

function setAuthorizationHeader(
    headers: InternalAxiosRequestConfig['headers'],
    token: string,
): void {
    if (!headers) return;
    const value = `Bearer ${token}`;
    if (headers instanceof AxiosHeaders) {
        headers.set('Authorization', value);
    } else {
        (headers as Record<string, string>)['Authorization'] = value;
    }
}

/** Убрать старый Bearer, чтобы при повторе request-интерцептор подставил актуальный токен из localStorage. */
function stripAuthorizationHeaders(headers: InternalAxiosRequestConfig['headers']): void {
    if (!headers) return;
    if (headers instanceof AxiosHeaders) {
        headers.delete('Authorization');
        headers.delete('authorization');
    } else {
        const h = headers as Record<string, unknown>;
        delete h.Authorization;
        delete h.authorization;
    }
}

function buildRetryConfig(
    original: InternalAxiosRequestConfig & { _retry?: boolean; skipAuthRefresh?: boolean },
): InternalAxiosRequestConfig {
    const next: InternalAxiosRequestConfig = {
        ...original,
        signal: undefined,
    };
    stripAuthorizationHeaders(next.headers);
    return next;
}

function decodeJwtPayload(token: string): JwtPayload | null {
    try {
        const part = token.split('.')[1];
        if (!part) return null;
        const normalized = part.replace(/-/g, '+').replace(/_/g, '/');
        const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
        return JSON.parse(atob(padded)) as JwtPayload;
    } catch {
        return null;
    }
}

function isSellerUserAccessToken(token: string): boolean {
    const payload = decodeJwtPayload(token);
    if (!payload) return false;
    if (payload.type === 'user') return true;
    if (payload.type === 'admin') return false;
    return Boolean(payload.phone) && !payload.email;
}

function sellerLoginPath(): string {
    const base = import.meta.env.BASE_URL || '/';
    return `${base.endsWith('/') ? base : `${base}/`}login`;
}

function redirectToLogin(reason: string, detail?: string, report = true): void {
    console.warn('[seller-auth] redirect to login:', reason, detail ?? '');
    if (report) reportSellerSessionDrop(reason, detail);
    const loginPath = sellerLoginPath();
    // Нельзя replace('/login') — уйдёт на чужой origin (супер-админка на admin.*)
    if (!window.location.pathname.endsWith('/login')) {
        window.location.replace(loginPath);
    }
}

/** Штатно: продавец не заходил дольше срока жизни refresh-токена. */
function isRefreshTokenExpired(error: unknown): boolean {
    return (
        axios.isAxiosError(error) &&
        (error.response?.data as { message?: unknown } | undefined)?.message ===
            'Refresh token expired'
    );
}

function describeRefreshError(error: unknown): string {
    if (axios.isAxiosError(error)) {
        if (error.response) return `http_${error.response.status}`;
        return error.code ?? 'no_response';
    }
    return error instanceof Error ? error.message : 'unknown';
}

function clearSellerSession(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
}

function getAccessToken(): string | null {
    const current = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (current) {
        if (isSellerUserAccessToken(current)) return current;
        // Админский JWT в seller-ключе — не используем
        localStorage.removeItem(ACCESS_TOKEN_KEY);
    }

    const legacy = localStorage.getItem(LEGACY_ACCESS_TOKEN_KEY);
    if (legacy && isSellerUserAccessToken(legacy)) {
        localStorage.setItem(ACCESS_TOKEN_KEY, legacy);
        localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
        return legacy;
    }
    if (legacy) {
        // Не тащим админский access_token из общей админки
        localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
    }
    return null;
}

function setAccessToken(token: string): void {
    if (!isSellerUserAccessToken(token)) {
        throw new Error('Seller cabinet accepts only user access tokens');
    }
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
    localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
    setSentrySellerUser(decodeJwtPayload(token)?.sub);
}

function removeAccessToken(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
}

function getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
}

function setRefreshToken(token: string): void {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
}

function removeRefreshToken(): void {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
}

function isSellerOnlyForbiddenMessage(message: unknown): boolean {
    if (typeof message !== 'string') return false;
    return (
        message.includes('Доступ только для продавца') ||
        message.includes('войдите по номеру телефона')
    );
}

function isSellerCabinetBlockedMessage(message: unknown): boolean {
    if (typeof message === 'string') {
        return message.includes('Кабинет продавца заблокирован');
    }
    if (Array.isArray(message)) {
        return message.some((item) => isSellerCabinetBlockedMessage(item));
    }
    return false;
}

let sellerCabinetBlockNotified = false;

function notifySellerCabinetBlocked(): void {
    if (sellerCabinetBlockNotified) return;
    sellerCabinetBlockNotified = true;
    window.dispatchEvent(new Event('seller-cabinet-blocked'));
}

/**
 * Кабинет продавца не использует cookie-сессию админки.
 * withCredentials: false — иначе cookie refreshToken с localhost:3000
 * уходит и на :3002 (cookies не разделяются по порту).
 */
export const axiosInstance: AxiosInstance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'X-Client': 'seller-admin',
    },
    timeout: 10000,
    withCredentials: false,
});

/** Успешный refresh: страницы, которые уже успели записать ошибку, перезапрашивают данные. */
export const SELLER_AUTH_REFRESHED_EVENT = 'seller-auth-refreshed';

let refreshPromise: Promise<string> | null = null;

const REFRESH_LOCK_NAME = 'luxus-seller-auth-refresh';

/**
 * RT общий для всех вкладок (localStorage), а бэкенд удаляет старый RT при ротации.
 * Без межвкладочной блокировки вторая вкладка шлёт уже удалённый RT и получает 401.
 */
async function withCrossTabRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
    if (typeof navigator !== 'undefined' && navigator.locks?.request) {
        return (await navigator.locks.request(REFRESH_LOCK_NAME, fn)) as T;
    }
    return fn();
}

/** Сессия мертва только при явном 401 от сервера; сеть, таймаут и 5xx — временные сбои. */
function isSessionRejected(error: unknown): boolean {
    if (axios.isAxiosError(error)) return error.response?.status === 401;
    return true;
}

/** Соседняя вкладка уже сменила RT — берём её свежий access token вместо своего refresh. */
function takeTokenRefreshedByOtherTab(usedRefreshToken: string): string | null {
    const latest = getRefreshToken();
    if (!latest || latest === usedRefreshToken) return null;
    const accessToken = getAccessToken();
    if (!accessToken) return null;
    window.dispatchEvent(new Event(SELLER_AUTH_REFRESHED_EVENT));
    return accessToken;
}

/** Один inflight refresh — иначе параллельные 401 после 15m ротируют RT дважды и второй кикает на логин. */
const refreshAccessToken = (): Promise<string> => {
    if (refreshPromise) {
        return refreshPromise;
    }

    const initialRefreshToken = getRefreshToken();
    if (!initialRefreshToken) {
        clearSellerSession();
        redirectToLogin('refresh token missing in localStorage');
        return Promise.reject(new Error('Seller refresh token missing'));
    }
    let sentRefreshToken = initialRefreshToken;

    refreshPromise = withCrossTabRefreshLock(async () => {
        const fromOtherTab = takeTokenRefreshedByOtherTab(initialRefreshToken);
        if (fromOtherTab) return fromOtherTab;

        const refreshToken = getRefreshToken();
        if (!refreshToken) {
            throw new Error('Seller refresh token removed by another tab');
        }
        sentRefreshToken = refreshToken;

        const response = await axiosInstance.post(
            '/auth/refresh',
            { refresh_token: refreshToken },
            {
                skipAuthRefresh: true,
                withCredentials: false,
            } as InternalAxiosRequestConfig & { skipAuthRefresh?: boolean },
        );
        const newAccessToken = response.data?.access_token;
        const newRefreshToken = response.data?.refresh_token;
        if (!newAccessToken || typeof newAccessToken !== 'string') {
            throw new Error('Refresh response has no access_token');
        }
        if (!isSellerUserAccessToken(newAccessToken)) {
            throw new Error('Refresh returned non-user access token');
        }
        if (!newRefreshToken || typeof newRefreshToken !== 'string') {
            throw new Error('Refresh response has no refresh_token');
        }
        setAccessToken(newAccessToken);
        setRefreshToken(newRefreshToken);
        console.info('[seller-auth] access token refreshed');
        window.dispatchEvent(new Event(SELLER_AUTH_REFRESHED_EVENT));
        return newAccessToken;
    })
        .catch((error) => {
            if (!isSessionRejected(error)) {
                console.warn(
                    '[seller-auth] refresh failed, session kept:',
                    describeRefreshError(error),
                );
                throw error;
            }
            // Браузеры без Web Locks: соседняя вкладка могла сменить RT, пока шёл наш запрос
            const fromOtherTab = takeTokenRefreshedByOtherTab(sentRefreshToken);
            if (fromOtherTab) return fromOtherTab;

            clearSellerSession();
            redirectToLogin(
                'refresh request failed',
                describeRefreshError(error),
                !isRefreshTokenExpired(error),
            );
            throw error;
        })
        .finally(() => {
            refreshPromise = null;
        });

    return refreshPromise;
};

async function refreshAndRetry(
    originalRequest: InternalAxiosRequestConfig & { _retry?: boolean; skipAuthRefresh?: boolean },
) {
    originalRequest._retry = true;
    try {
        await refreshAccessToken();
    } catch (refreshErr) {
        return Promise.reject(refreshErr);
    }

    try {
        return await axiosInstance.request(buildRetryConfig(originalRequest));
    } catch (retryErr) {
        const st = axios.isAxiosError(retryErr) ? retryErr.response?.status : undefined;
        // Только явный 401 после refresh = сессия мертва.
        // Таймаут/сеть (нет response) раньше ошибочно кикали на логин.
        if (st === 401) {
            clearSellerSession();
            redirectToLogin('still 401 after refresh+retry');
            return Promise.reject(retryErr);
        }
        if (
            st === 403 &&
            isSellerOnlyForbiddenMessage(
                axios.isAxiosError(retryErr) ? retryErr.response?.data?.message : undefined,
            )
        ) {
            clearSellerSession();
            redirectToLogin('403 seller-only after successful refresh');
        }
        return Promise.reject(retryErr);
    }
}

axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
            // После сжатия обычно быстро; запас на медленную сеть / S3
            if (config.timeout == null || config.timeout === 10000) {
                config.timeout = 60 * 1000;
            }
        }

        const token = getAccessToken();
        if (token && config.headers) {
            setAuthorizationHeader(config.headers, token);
        }

        return config;
    },
    (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<{ message?: string }>) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & {
            _retry?: boolean;
            skipAuthRefresh?: boolean;
        };

        const requestUrl = originalRequest?.url || '';
        const isRefreshRequest =
            requestUrl.includes('/auth/refresh') ||
            requestUrl.endsWith('auth/refresh') ||
            originalRequest?.skipAuthRefresh === true;

        // Решение о выходе принимает refreshAccessToken: сначала проверяет, не обновила ли RT соседняя вкладка
        if (isRefreshRequest && error.response?.status === 401) {
            return Promise.reject(error);
        }

        // 401 → seamless refresh seller-токеном из localStorage (не cookie админки)
        if (error.response?.status === 401 && !originalRequest?._retry && !isRefreshRequest) {
            return refreshAndRetry(originalRequest);
        }

        // Админский access в LS даёт 403, не 401 — пробуем поднять seller-сессию через refresh
        if (
            error.response?.status === 403 &&
            !originalRequest?._retry &&
            !isRefreshRequest &&
            isSellerOnlyForbiddenMessage(error.response.data?.message) &&
            getRefreshToken()
        ) {
            return refreshAndRetry(originalRequest);
        }

        if (
            error.response?.status === 403 &&
            isSellerOnlyForbiddenMessage(error.response.data?.message) &&
            !getRefreshToken()
        ) {
            clearSellerSession();
            redirectToLogin('403 seller-only and no refresh token');
            return Promise.reject(error);
        }

        if (
            error.response?.status === 403 &&
            isSellerCabinetBlockedMessage(error.response.data?.message)
        ) {
            notifySellerCabinetBlocked();
        }

        if (error.response) {
            const status = error.response.status;
            const message = error.response.data?.message || error.message;
            switch (status) {
                case 400:
                    console.error('Bad Request:', message);
                    break;
                case 403:
                    console.error('Forbidden:', message);
                    break;
                case 404:
                    console.error('Not Found:', message);
                    break;
                case 500:
                    console.error('Server Error:', message);
                    break;
                default:
                    console.error('Error:', message);
            }
        } else if (error.request) {
            console.error('Network Error:', error.message);
        } else {
            console.error('Error:', error.message);
        }

        return Promise.reject(error);
    },
);

const initialAccessToken = getAccessToken();
if (initialAccessToken) {
    setSentrySellerUser(decodeJwtPayload(initialAccessToken)?.sub);
}

export const authUtils = {
    getAccessToken,
    setAccessToken,
    removeAccessToken,
    getRefreshToken,
    setRefreshToken,
    removeRefreshToken,
    clearSellerSession,
    markManualLogout: suppressSellerSessionDropReports,
};

export default axiosInstance;
